/* The one place that talks to the server.
 *
 * Every call goes through `request`, so error handling exists once. FastAPI
 * puts its message in `detail` — a 409 from the workflow guard says exactly
 * what transition was refused, and throwing away that string in favour of
 * "Request failed" would turn a diagnosable refusal into a mystery. */

import type {
  AccuracyReport, AppSettings, CheckReport, Highlights, JobAccuracy, JobDetail, JobSummary, Subtask, SubtaskEvidence, SubtaskPatch,
  SubtaskStatus, Task, VideoRow,
} from "./types";

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    // Network-level: the API is not running, or the machine went offline.
    // Distinguished from an HTTP error because the remedy is different.
    throw new ApiError(0, "Cannot reach the server. Is the API running?");
  }

  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      // 422 from pydantic is an array of field errors; 4xx from our own guards
      // is a plain string. Both live under `detail`.
      if (typeof body.detail === "string") detail = body.detail;
      else if (Array.isArray(body.detail) && body.detail.length)
        detail = body.detail.map((e: { msg: string }) => e.msg).join("; ");
    } catch {
      /* a non-JSON error body; statusText is all there is */
    }
    throw new ApiError(res.status, detail);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: "POST", body: JSON.stringify(body ?? {}) });

export const api = {
  health: () => request<{ ok: boolean; database: string }>("/health"),

  // -- reading ------------------------------------------------------------
  jobs: (params?: { stage?: string; status?: string }) => {
    const q = new URLSearchParams();
    if (params?.stage) q.set("stage", params.stage);
    if (params?.status) q.set("status", params.status);
    const s = q.toString();
    return request<JobSummary[]>(`/api/jobs${s ? `?${s}` : ""}`);
  },
  /** The corpus: every video, processed or not. */
  videos: (params?: { category?: string; sub_category?: string }) => {
    const q = new URLSearchParams();
    if (params?.category) q.set("category", params.category);
    if (params?.sub_category) q.set("sub_category", params.sub_category);
    const s = q.toString();
    return request<VideoRow[]>(`/api/videos${s ? `?${s}` : ""}`);
  },

  accuracy: (gt?: string, evalName?: string) => {
    const q = new URLSearchParams();
    if (gt) q.set("gt", gt);
    if (evalName) q.set("eval", evalName);
    const s = q.toString();
    return request<AccuracyReport>(`/api/accuracy${s ? `?${s}` : ""}`);
  },
  jobAccuracy: (jobId: number) => request<JobAccuracy>(`/api/accuracy/jobs/${jobId}`),
  job: (id: number) => request<JobDetail>(`/api/jobs/${id}`),
  checks: (id: number) => request<CheckReport>(`/api/jobs/${id}/checks`),
  /** Object and hand boxes for the overlay; `{available: false}` when the
   *  video has none yet (never a 404). */
  highlights: (id: number) => request<Highlights>(`/api/jobs/${id}/highlights`),
  /** Tool-wide switches (4 Nov 2026): highlights on/off for every user and video. */
  settings: () => request<AppSettings>("/api/settings"),
  setHighlights: (enabled: boolean, by?: string) =>
    request<AppSettings>("/api/settings/highlights",
      { method: "PUT", body: JSON.stringify({ enabled, by }) }),

  // -- editing ------------------------------------------------------------
  patch: (subtaskId: number, patch: SubtaskPatch) =>
    request<Subtask>(`/api/subtasks/${subtaskId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /** Returns BOTH neighbours — a caller that re-renders only one shows a gap
   *  that does not exist in the database. */
  moveBoundary: (jobId: number, afterSubtaskId: number, atSeconds: number) =>
    post<Subtask[]>(`/api/jobs/${jobId}/boundary`, {
      after_subtask_id: afterSubtaskId,
      at_seconds: atSeconds,
    }),

  /** Title and description only — the span comes from the sub-tasks. */
  patchTask: (taskId: number, patch: { title?: string; description?: string | null }) =>
    request<Task>(`/api/tasks/${taskId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }),

  split: (subtaskId: number, atSeconds: number) =>
    post<Subtask[]>("/api/subtasks/split", {
      subtask_id: subtaskId,
      at_seconds: atSeconds,
    }),

  merge: (subtaskId: number) =>
    post<Subtask>("/api/subtasks/merge", { subtask_id: subtaskId }),

  reset: (subtaskId: number) =>
    post<Subtask>(`/api/subtasks/${subtaskId}/reset`),

  /** Why the judge decided what it decided. GET and side-effect free, so it is
   *  safe to call on every selection; a finished verification run never
   *  rewrites these rows. Its own route because the job document would double
   *  in size carrying them for every sub-task at once. */
  evidence: (subtaskId: number) =>
    request<SubtaskEvidence>(`/api/subtasks/${subtaskId}/evidence`),

  review: (subtaskId: number, status: SubtaskStatus, reason?: string) =>
    post<Subtask>(`/api/subtasks/${subtaskId}/review`, {
      status,
      rejection_reason: reason ?? null,
    }),

  // -- workflow -----------------------------------------------------------
  start: (jobId: number) => post<JobDetail>(`/api/jobs/${jobId}/start`),

  /** The review checklist's by-hand ticks. PUT: the whole list every time, so
   *  two tabs cannot interleave into a half-ticked state. */
  setManualChecks: (jobId: number, values: boolean[]) =>
    request<JobDetail>(`/api/jobs/${jobId}/manual-checks`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ values }),
    }),
  submit: (jobId: number, message?: string) =>
    post<JobDetail>(`/api/jobs/${jobId}/submit`, { message: message ?? null }),

  qaStart: (jobId: number) => post<JobDetail>(`/api/qa/jobs/${jobId}/start`),
  /** 26 Oct 2026: every processed video back to "Ready for review" for a demo —
   *  AI-approved sub-tasks stay done, the rest return to pending. */
  resetAllForDemo: () =>
    post<{ videos: number; subtasks_reopened: number; videos_moved_back: number }>(
      "/api/demo/reset-all"),
  qaApprove: (jobId: number, message?: string) =>
    post<JobDetail>(`/api/qa/jobs/${jobId}/approve`, { message: message ?? null }),
  qaReject: (jobId: number, message: string) =>
    post<JobDetail>(`/api/qa/jobs/${jobId}/reject`, { message }),
};
